import {
  Accordion,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import {
  IconBook,
  IconChevronRight,
  IconPlus,
  IconShieldCheck,
} from '@tabler/icons';
import { useEffect, useMemo, useState } from 'react';
import { EpiTutorial } from '../../components/epi-tutorial';

import { EpiCatalogPanel } from '../../components/epi-catalog-panel';
import { Page } from '../../components/page';
import {
  deleteSectorEpiRequirementRequest,
  listEpiCatalogRequest,
  listSectorEpiMatrixRequest,
  setSectorEpiRequirementRequest,
} from '../../data/employees';
import { Epi, SectorEpiRequirement } from '../../models/employees';
import {
  errorNotification,
  successNotification,
} from '../../providers/mantine-notifications';
import { SECTOR_OPTIONS, SECTORS } from '../../utils/constants/sectors';

const LS_KEY = 'epi_matrix_sectors';

function loadStoredSectors(): string[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveStoredSectors(list: string[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    /* empty */
  }
}

export function EpiMatrix() {
  const [epis, setEpis] = useState<Epi[]>([]);
  const [matrix, setMatrix] = useState<SectorEpiRequirement[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [newSector, setNewSector] = useState('');
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  async function loadData() {
    try {
      setLoading(true);
      const [catalogRes, matrixRes] = await Promise.all([
        listEpiCatalogRequest(),
        listSectorEpiMatrixRequest(),
      ]);
      setEpis(catalogRes.data.filter((e) => e.isActive));
      setMatrix(matrixRes.data);
      const fromApi = Array.from(new Set(matrixRes.data.map((r) => r.sector)));
      const fromStorage = loadStoredSectors();
      const merged = Array.from(new Set([...fromStorage, ...fromApi]));
      setSectors(merged);
      saveStoredSectors(merged);
    } catch {
      errorNotification({
        title: 'Erro ao carregar matriz.',
        message: 'Tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function getRequirement(sector: string, epiId: number) {
    return matrix.find((r) => r.sector === sector && r.epi.id === epiId);
  }

  async function handleToggle(sector: string, epi: Epi) {
    const key = `${sector}-${epi.id}`;
    const existing = getRequirement(sector, epi.id);
    try {
      setToggling(key);
      if (existing) {
        await deleteSectorEpiRequirementRequest(existing.id);
        setMatrix((prev) => prev.filter((r) => r.id !== existing.id));
        successNotification({
          title: 'EPI removido da matriz.',
          message: `${epi.name} não é mais obrigatório para ${sector}.`,
        });
      } else {
        const res = await setSectorEpiRequirementRequest({
          sector,
          epiId: epi.id,
          required: true,
        });
        setMatrix((prev) => [...prev, { ...res.data, epi }]);
        successNotification({
          title: 'EPI adicionado à matriz.',
          message: `${epi.name} agora é obrigatório para ${sector}.`,
        });
      }
    } catch {
      errorNotification({
        title: 'Erro ao atualizar.',
        message: 'Tente novamente.',
      });
    } finally {
      setToggling(null);
    }
  }

  function handleAddSector() {
    const name = newSector.trim();
    if (!name) return;
    if (sectors.includes(name)) {
      errorNotification({
        title: 'Setor já existe.',
        message: 'Use um nome diferente.',
      });
      return;
    }
    setSectors((prev) => {
      const updated = [...prev, name];
      saveStoredSectors(updated);
      return updated;
    });
    setNewSector('');
    successNotification({
      title: 'Setor adicionado.',
      message: 'Configure os EPIs abaixo.',
    });
  }

  function episForSector(sector: string): Epi[] {
    return epis.filter((epi) => {
      if (!epi.sectors) return false;
      return epi.sectors
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .includes(sector.toLowerCase());
    });
  }

  function countRequired(sector: string) {
    return matrix.filter((r) => r.sector === sector).length;
  }

  const sectorSelectData = useMemo(
    () => [
      ...SECTOR_OPTIONS,
      ...sectors
        .filter((s) => !SECTORS.includes(s))
        .map((s) => ({ value: s, label: s })),
    ],
    [sectors],
  );

  return (
    <Page>
      <Stack spacing={16} p={16}>
        <Group position="apart" align="flex-start">
          <div>
            <Title>Matriz Setor × EPI</Title>
            <Text color="gray">
              defina quais EPIs são obrigatórios para cada setor
            </Text>
          </div>
          <Group spacing={8} mt={4}>
            <EpiTutorial screen="matrix" />
            <Button
              leftIcon={<IconBook size={16} />}
              variant="light"
              onClick={() => setCatalogOpen(true)}
            >
              Ver Catálogo
            </Button>
          </Group>
        </Group>

        <Group spacing={8} align="flex-end">
          <Select
            label="Adicionar setor"
            placeholder="Selecione ou digite o nome"
            data={sectorSelectData}
            searchable
            creatable
            getCreateLabel={(q) => `+ Adicionar "${q}"`}
            onCreate={(q) => {
              setNewSector(q);
              return { value: q, label: q };
            }}
            value={newSector || null}
            onChange={(v) => setNewSector(v ?? '')}
            style={{ width: 320 }}
          />
          <Button
            leftIcon={<IconPlus size={16} />}
            onClick={handleAddSector}
            mb={1}
          >
            Adicionar
          </Button>
          {sectors.length > 0 && (
            <Group spacing={16} mb={1} ml={8}>
              <Text size="sm" color="dimmed">
                <Text span fw="bold" color="dark">
                  {sectors.length}
                </Text>{' '}
                setores
              </Text>
              <Text size="sm" color="dimmed">
                <Text span fw="bold" color="dark">
                  {matrix.length}
                </Text>{' '}
                requisitos
              </Text>
            </Group>
          )}
        </Group>

        {loading ? (
          <Group position="center" py={48}>
            <Loader />
          </Group>
        ) : sectors.length === 0 ? (
          <Stack align="center" py={48} spacing={8}>
            <IconShieldCheck size={40} color="#adb5bd" />
            <Text fw="bold" color="dimmed">
              Nenhum setor cadastrado
            </Text>
            <Text size="sm" color="dimmed">
              Adicione um setor acima para começar a configurar os EPIs
              obrigatórios.
            </Text>
          </Stack>
        ) : (
          <Accordion
            variant="separated"
            chevron={<IconChevronRight size={16} />}
          >
            {sectors.map((sector) => {
              const sectorEpis = episForSector(sector);
              const required = countRequired(sector);
              return (
                <Accordion.Item
                  key={sector}
                  value={sector}
                  sx={(theme) => ({
                    border: `1px solid ${theme.colors.gray[3]}`,
                    borderRadius: theme.radius.sm,
                  })}
                >
                  <Accordion.Control>
                    <Group position="apart" pr={8}>
                      <Text fw={600}>{sector}</Text>
                      <Group spacing={8}>
                        {sectorEpis.length === 0 ? (
                          <Badge color="gray" variant="light" size="sm">
                            Nenhum EPI vinculado no catálogo
                          </Badge>
                        ) : (
                          <Badge color="blue" variant="light" size="sm">
                            {sectorEpis.length} EPI
                            {sectorEpis.length !== 1 ? 's' : ''} disponíveis
                          </Badge>
                        )}
                        {required > 0 && (
                          <Badge color="teal" variant="filled" size="sm">
                            {required} obrigatório{required !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </Group>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {sectorEpis.length === 0 ? (
                      <Text size="sm" color="dimmed" py={8}>
                        Nenhum EPI foi cadastrado no catálogo para o setor{' '}
                        <strong>{sector}</strong>. Abra o Catálogo de EPIs e
                        informe este setor no campo "Setores/Cargos que
                        utilizam".
                      </Text>
                    ) : (
                      <Stack spacing={12}>
                        {sectorEpis.map((epi) => {
                          const key = `${sector}-${epi.id}`;
                          const checked = !!getRequirement(sector, epi.id);
                          return (
                            <Group
                              key={epi.id}
                              position="apart"
                              px={12}
                              py={10}
                              sx={(theme) => ({
                                border: `1px solid ${theme.colors.gray[3]}`,
                                borderRadius: theme.radius.sm,
                                backgroundColor: theme.white,
                              })}
                            >
                              <div>
                                <Text size="sm" fw={500}>
                                  {epi.name}
                                </Text>
                                <Text size="xs" color="dimmed">
                                  {[
                                    epi.category,
                                    epi.brand,
                                    epi.ca ? `CA ${epi.ca}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </Text>
                              </div>
                              <Switch
                                checked={checked}
                                disabled={toggling === key}
                                onChange={() => handleToggle(sector, epi)}
                                size="sm"
                                color="teal"
                                label={checked ? 'Obrigatório' : 'Opcional'}
                                aria-label={`${epi.name} obrigatório para ${sector}`}
                              />
                            </Group>
                          );
                        })}
                      </Stack>
                    )}
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Stack>

      <Drawer
        opened={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        position="right"
        size="80%"
        padding="lg"
        title={null}
        styles={{
          drawer: { display: 'flex', flexDirection: 'column' },
          body: { flex: 1, overflow: 'auto', padding: '24px' },
          header: { display: 'none' },
        }}
      >
        <EpiCatalogPanel onSaved={loadData} />
      </Drawer>
    </Page>
  );
}
